<template>
  <div
    id="select-another-overlay-modal"
    :class="classes"
  >
    <div class="overlay__header-container">
      <div class="container">
        <div class="overlay__header">
          <div class="row">
            <div class="col-sm-6">
              <h2>
                <contentful-text :item="contentfulText('select-photo-modal-main-title')"/>
              </h2>
              <h3 class="overlay__copy hidden-xs">
                <contentful-text :item="contentfulText('select-photo-modal-lede')"/>
              </h3>
            </div>
            <div class="col-sm-4 col-sm-offset-2">
              <button
                type="button"
                class="button button--fw overlay__close-button"
                @click="showModal = false"
              >
                <contentful-text :item="contentfulText('select-photo-modal-close-button-label')"/>
              </button>
              <div
                v-if="viewModel.ShowRetouchingCopy"
                class="hidden-xs"
                style="padding-top: 5px;"
              >
                <i>
                  <contentful-text :item="contentfulText('select-photo-modal-retouching-notice')"/>
                </i>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    <div
      v-if="viewModel.ShowRetouchingCopy"
      class="container visible-xs"
    >
      <div class="row">
        <div class="col-xs-12">
          <p><contentful-text :item="contentfulText('select-photo-modal-retouching-notice')"/></p>
        </div>
      </div>
    </div>
    <div class="container">
      <div class="overlay__grid">
        <div
          v-masonry
          v-if="viewModel.ShowFavoriteFrames"
          :item-selector="getMasonryItemSelector(viewModel.FavoriteFrames)"
        >
          <frame-image
            v-masonry-tile
            v-for="frame in filterFrames(viewModel.FavoriteFrames)"
            :frame-info="frame"
            :key="frame.FrameID"
            @image-click="handleImageClick"
          />
        </div>
        <h3 v-if="viewModel.ShowImageGroupLabels">
          <contentful-text :item="contentfulText('select-photo-modal-event-images-label')"/>
        </h3>
        <div
          v-masonry
          :item-selector="getMasonryItemSelector(viewModel.EventFrames)"
        >
          <frame-image
            v-masonry-tile
            v-for="frame in filterFrames(viewModel.EventFrames)"
            :frame-info="frame"
            :key="frame.FrameID"
            @image-click="handleImageClick"
          />
        </div>
        <div v-if="viewModel.ShowHighlightFrames">
          <div
            v-for="(highlightFramesGroup, index) in viewModel.HighlightFrames"
            :key="index"
          >
            <h3>{{ highlightFramesGroup.CategoryName }}</h3>
            <div
              v-masonry
              :item-selector="getMasonryItemSelector(highlightFramesGroup.Frames)"
            >
              <frame-image
                v-masonry-tile
                v-for="frame in filterFrames(highlightFramesGroup.Frames)"
                :frame-info="frame"
                :key="frame.FrameID"
                @image-click="handleImageClick"
              />
            </div>
          </div>
        </div>
      </div>
    </div>
    <div
      class="container"
    >
      <p v-if="noMatchingOrientationFrames">
        <contentful-text :item="contentfulText('select-photo-modal-no-matching-orientation-warning')"/>
      </p>
      <div class="row">
        <div class="col-xs-12">
          <p><i>*<contentful-text :item="contentfulText('select-photo-modal-automatic-crop-notice')"/></i></p>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import FrameImage from './FrameImage.vue'
import { some, filter } from 'lodash'
import ContentfulMixin from '@Shared/mixins/contentful'

export default {
  name: 'SelectPhotoModal',
  components: {
    FrameImage
  },
  mixins: [
    ContentfulMixin
  ],
  props: {
    show: { type: Boolean, default: false },
    legacyMode: { type: Boolean, default: false } // when true will enable events and events listeners to interact with legacy JS
  },
  data () {
    return {
      showModal: false,
      orientationRestriction: null,
      imgSelectAction: null
    }
  },
  computed: {
    viewModel () {
      return this.$store.state.catalogPageCommon.selectDifferentPhotoViewModel
    },
    classes () {
      return {
        'overlay': true,
        'overlay__select-another': true,
        'overlay--with-grid': true,
        'overlay--visible': this.showModal
      }
    },
    noMatchingOrientationFrames () {
      return this.orientationRestriction !== null && this.filterFrames(this.viewModel.EventFrames).length === 0
    },
    content () {
      return this.$store.state.catalogPageCommon.selectPhotoModalContent
    }
  },
  watch: {
    showModal (v, ov) {
      this.toggleBodyOverlayClass(v)

      if (v === true) {
        this.$redrawVueMasonry()
      } else {
        this.$emit('close')
      }
    },
    show (v, ov) {
      this.showModal = v
    }
  },
  created () {
    this.$redrawVueMasonry()

    if (this.legacyMode === true) { // only init the event listeners for events fired from legacy JS if in legacy mode
      document.addEventListener('selectPhotoModal.show', ev => {
        this.showModal = true
        this.orientationRestriction = ev.detail.orientationRestriction
        this.imgSelectAction = ev.detail.action
      })

      document.addEventListener('selectPhotoModal.hide', e => {
        this.showModal = false
      })
    }
  },
  mounted () {
    this.showModal = this.show // initialize the data property that controls the modal visibility with what the props says by default
  },
  methods: {
    getMasonryItemSelector (frames) {
      let somePortraitFrames = some(frames, f => {
        return f.Orientation === 1 // 1 = vertical/portrait
      })

      let someLandscapeFrames = some(frames, f => {
        return f.Orientation === 0 // 0 = horizontal/landscape
      })

      if (somePortraitFrames) {
        return '.select-another-grid-item'
      } else if (!somePortraitFrames && someLandscapeFrames) {
        return '.select-another-grid-item--width-2'
      }

      return '.select-another-grid-item' // shouldn't get here, but just in case
    },
    handleImageClick (data) {
      this.$emit('photo-selected', data)

      if (this.legacyMode === true) { // only fire this event in legacy mode
        document.dispatchEvent(new CustomEvent('selectedPhotoModal.imageSelected', {
          detail: {
            selectedImage: data,
            action: this.imgSelectAction
          }
        }))
      }

      this.showModal = false
    },
    filterFrames (frames) {
      if (this.orientationRestriction === null) {
        return frames
      }

      return filter(frames, f => {
        if (f.Orientation === 0 && this.orientationRestriction === 'landscape') {
          return true
        } else if (f.Orientation === 1 && this.orientationRestriction === 'portrait') {
          return true
        }

        return false
      })
    },
    toggleBodyOverlayClass (isVisible) {
      if (isVisible === true) {
        document.body.classList.add('body__overlay--visible')
      } else {
        document.body.classList.remove('body__overlay--visible')
      }
    }
  }
}
</script>

<style>
</style>



// WEBPACK FOOTER //
// WebResources/vue/shared/components/catalog/SelectPhotoModal/SelectPhotoModal.vue